import { useCallback, useMemo, useState } from 'react';

import type { GridRowSelectionModel } from '@mui/x-data-grid-premium';
import type { DocumentMetadataImportHistoryFieldsFragment } from 'src/gql/generated/apollo-types';
import * as Apollo from 'src/gql/generated/apollo-types';
import { useOnDocumentMetadataImportProgressSubscription } from 'src/gql/generated/apollo-types';
import { useNotificationDispatch } from 'src/hooks';
import { NotificationsActionType } from 'src/notifications';

import { getDeletedImportsMessage, getIsCommittedActiveImport } from './data-migration-status';

export function useDataMigrationHistory() {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectionModel, setSelectionModel] =
    useState<GridRowSelectionModel>(createEmptySelectionModel);
  const { data, error, loading } = Apollo.useDocumentMetadataImportsQuery({
    fetchPolicy: 'cache-and-network',
  });

  /**
   * Progress events return the DocumentMetadataImport entity, which Apollo
   * writes to the cache and normalizes by id automatically — every cached row
   * (list and detail) updates without any handler.
   */
  useOnDocumentMetadataImportProgressSubscription();

  // Shallow-copy rows because cache objects are frozen and the MUI grid needs mutable rows.
  const imports = useMemo(
    () =>
      (data?.documentMetadataImports ?? []).map(importRecord => ({
        ...importRecord,
        summary: importRecord.summary ? { ...importRecord.summary } : importRecord.summary,
      })),
    [data?.documentMetadataImports]
  );

  const initialLoading = loading && !data;
  const [deleteImports, { loading: deleting }] = Apollo.useDeleteMetadataImportsMutation();
  const dispatch = useNotificationDispatch();
  const historyState = getDataMigrationHistoryState({ imports, selectionModel });

  const closeDeleteConfirm = useCallback(() => {
    setDeleteConfirmOpen(false);
  }, []);
  const openDeleteConfirm = useCallback(() => {
    setDeleteConfirmOpen(true);
  }, []);
  const handleDeleteConfirm = useCallback(async () => {
    if (historyState.selectedIds.length === 0) return;

    const deletedCount = historyState.selectedIds.length;
    setSelectionModel(createEmptySelectionModel());
    setDeleteConfirmOpen(false);

    try {
      await deleteImports({
        optimisticResponse: {
          __typename: 'Mutation',
          deleteMetadataImports: {
            __typename: 'DeleteMetadataImportsResult',
            deletedIds: historyState.selectedIds,
          },
        },
        update: (cache, { data: result }) => {
          const deletedIds = new Set(result?.deleteMetadataImports.deletedIds ?? []);
          if (deletedIds.size === 0) return;

          cache.updateQuery<Apollo.DocumentMetadataImportsQuery>(
            { query: Apollo.DocumentMetadataImportsDocument },
            existing =>
              existing
                ? {
                    ...existing,
                    documentMetadataImports: existing.documentMetadataImports.filter(
                      row => !deletedIds.has(row.id)
                    ),
                  }
                : existing
          );
        },
        variables: { importIds: historyState.selectedIds },
      });
      dispatch({
        payload: {
          message: getDeletedImportsMessage(deletedCount),
          variant: 'success',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
    } catch (_error) {
      // Apollo rolls back the optimistic list removal automatically.
      dispatch({
        payload: {
          message: 'Failed to delete data migration(s).',
          variant: 'error',
        },
        type: NotificationsActionType.PUSH_NOTIFICATION,
      });
    }
  }, [deleteImports, dispatch, historyState.selectedIds]);

  return {
    ...historyState,
    closeDeleteConfirm,
    deleteConfirmOpen,
    deleting,
    error,
    handleDeleteConfirm,
    imports,
    initialLoading,
    openDeleteConfirm,
    selectionModel,
    setSelectionModel,
  };
}

function createEmptySelectionModel(): GridRowSelectionModel {
  return {
    ids: new Set(),
    type: 'include',
  };
}

function getDataMigrationHistoryState(options: {
  imports: DocumentMetadataImportHistoryFieldsFragment[];
  selectionModel: GridRowSelectionModel;
}) {
  const { imports, selectionModel } = options;
  const selectedIds =
    selectionModel.type === 'exclude'
      ? imports.filter(row => !selectionModel.ids.has(row.id)).map(row => row.id)
      : Array.from(selectionModel.ids, id => String(id));
  const selectedImportId = selectedIds.length === 1 ? selectedIds[0] : null;
  const selectedImport = selectedImportId
    ? imports.find(importRecord => importRecord.id === selectedImportId)
    : null;
  const selectedImportIsRunning = selectedImport
    ? getIsCommittedActiveImport(selectedImport)
    : false;
  const hasRunningImport = imports.some(importRecord => getIsCommittedActiveImport(importRecord));

  return {
    hasImports: imports.length > 0,
    hasRunningImport,
    selectedCount: selectedIds.length,
    selectedIds,
    selectedImportId,
    selectedImportIsRunning,
  };
}
